import { toRaw } from "vue";
import BaseSchema from "./base";
import { utils } from "@socotec.io/socio-vue-components";
import _ from "lodash";
import useInstallationVle from "@/composables/installations/vles/useInstallationVle";

const { getDewPoint } = useInstallationVle();

class RecordBook extends BaseSchema {
  static primaryKey = "uuid";
  static tableName = "recordBooks";
  static compoundIndexes = [
    "[project+type]",
    "[project+installation]",
    "[installation+type]",
    "[phase+type]",
  ];
  static fields = [
    "uuid",
    "createdAt",
    "updatedAt",
    "type",
    "label",
    "eventTypes",
    "formTypes",
    "recordBlocks",
    "installation",
    "project",
    "speed",
    "humidity",
    "nozzleDiameter",
    "phase",
    "measurementMethod",
    "formFieldToEvent",
    "isArchived",
    "environmentalCondition",
    "manual",
    "phaseSignature",
    "samplingForm",
    "measurementForm",
    "recordForm",
    "finished",
  ];

  static relatedFields = {
    installationObj: { field: "installation", tableName: "installations" },
    environmentalConditionObj: {
      field: "environmentalCondition",
      tableName: "recordBooks",
    },
    speedObj: {
      field: "speed",
      tableName: "recordBooks",
    },
    phaseObj: {
      field: "phase",
      tableName: "phases",
    },
    measurementFormObj: {
      field: "measurementForm",
      tableName: "measurementForm",
    },
    nozzleDiameterObj: {
      field: "nozzleDiameter",
      tableName: "recordBooks",
    },
    humidityObj: {
      field: "humidity",
      tableName: "recordBooks",
    },
    manualObj: {
      field: "manual",
      tableName: "recordBooks",
    },
  };

  setDate() {
    if (this.type === "environmental_condition")
      this.date = utils.filters.formatDate(this.recordForm?.interventionDate);
    else {
      this.date = utils.filters.formatDate(
        this.environmentalConditionObj?.recordForm?.interventionDate
      );
    }
  }

  async setFilterTemperature() {
    await this.setRelationships();
    const h2oExpectedContent = this.installationObj?.h2oExpectedContent;
    let dewPoint = 0;
    if (h2oExpectedContent) dewPoint = getDewPoint(h2oExpectedContent);
    if (this.type != "manual_sampling") return;
    const parameters = await this.findParametersUsed(this.phase);

    const temperatures = parameters.map((p) =>
      p.getFilteringTemperature(this.recordForm.tubeMaterial, dewPoint)
    );
    const temp = temperatures.length ? Math.max(...temperatures) : 0;
    this.recordForm.theoricTemperature = temp;
    this.recordForm.realTemperature = temp;
    await this.saveRecordForm();
  }

  async setDurations() {
    if (this.type != "manual_sampling") return;
    const duration = await this.findMaxDuration(this.installation, this.phase);
    if (!duration) return;
    const [hours, minutes] = duration.split(":");
    this.recordForm.samplingDuration = `${hours}:${minutes}`;
    await this.saveRecordForm();
  }

  async saveRecordForm() {
    await this.db.recordBooks.update(this.uuid, {
      recordForm: toRaw(this.recordForm),
    });
  }
  static dateFields = ["createdAt", "updatedAt"];

  async findParametersUsed(phase) {
    const samplesUsed = await this.db.samples
      .where("phase")
      .equals(phase)
      .toArray();
    const parameterUsedUuids = _.flatten(
      samplesUsed.map((p) => [...p.mainParameters, p.secondParameter])
    ).filter((v) => v);

    return await this.db.parameters
      .where("uuid")
      .anyOf(parameterUsedUuids)
      .toArray();
  }

  async findVleUsed(installation, { phase = null, parametersUsed = null }) {
    if (!parametersUsed) {
      parametersUsed = await this.findParametersUsed(phase);
    }
    const parameterUsedUuids = parametersUsed.map(({ uuid }) => uuid);

    const mtxIsUsed = parametersUsed.some((p) =>
      ["MtxP", "MtxG"].includes(p.sampleShortName)
    );

    const configurationLineUuids = await this.db.configurationLines
      .where("parameter")
      .anyOf(parameterUsedUuids)
      .and((cl) => cl.installation == installation)
      .primaryKeys();

    let vlesQs = this.db.vles
      .where("configurationLine")
      .anyOf(configurationLineUuids);

    if (mtxIsUsed) {
      const sums = await this.db.sums
        .where("installation")
        .equals(installation)
        .primaryKeys();
      vlesQs = vlesQs.or("sum").anyOf(sums);
    }
    return await vlesQs.toArray();
  }

  async findMaxDuration(installation, phase) {
    const parametersUsed = await this.findParametersUsed(phase);
    const vles = await this.findVleUsed(installation, { parametersUsed });
    const vleMaxDuration = _.maxBy(vles, "samplingDuration")?.samplingDuration;
    const parameterMaxDuration = _.maxBy(
      parametersUsed,
      "sampleDuration"
    )?.sampleDuration;

    return vleMaxDuration > parameterMaxDuration
      ? vleMaxDuration
      : parameterMaxDuration;
  }

  getForm(recordBlockType) {
    const block = this.recordBlocks.find(
      (block) => block.type == recordBlockType
    );
    return block?.recordForm;
  }

  async getFullForm() {
    await this.setRelationships();
    return Object.assign({}, ...this.recordBlocks.map((b) => b.recordForm));
  }
}

export default RecordBook;
